const STORAGE_KEY = 'ai-canvas:op-preferences:v1'
const STORAGE_VERSION = 1
const ENCODED_UNDEFINED_MARKER = '__ai_canvas_op_preference_undefined__'
const OMIT_PREFERENCE_VALUE = Symbol('omit op preference value')

interface StoredOpPreferences {
  version: typeof STORAGE_VERSION
  ops: Record<string, Record<string, unknown>>
}

let shouldPersistToLocalStorage = false
let memoryStore = createEmptyStore()

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasLocalStorage() {
  return typeof window !== 'undefined' && Boolean(window.localStorage)
}

function createEmptyStore(): StoredOpPreferences {
  return {
    version: STORAGE_VERSION,
    ops: {},
  }
}

function isEncodedUndefined(value: unknown) {
  return (
    isRecord(value) &&
    value[ENCODED_UNDEFINED_MARKER] === true &&
    Object.keys(value).length === 1
  )
}

function encodeForJson(value: unknown): unknown {
  if (value === undefined) {
    return { [ENCODED_UNDEFINED_MARKER]: true }
  }

  if (Array.isArray(value)) {
    return value.map((item) => encodeForJson(item))
  }

  if (!isRecord(value)) {
    return value
  }

  const result: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, nestedValue]) => {
    result[key] = encodeForJson(nestedValue)
  })
  return result
}

function decodeFromJson(value: unknown): unknown {
  if (isEncodedUndefined(value)) {
    return undefined
  }

  if (Array.isArray(value)) {
    return value.map((item) => decodeFromJson(item))
  }

  if (!isRecord(value)) {
    return value
  }

  const result: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, nestedValue]) => {
    result[key] = decodeFromJson(nestedValue)
  })
  return result
}

function readLocalStore(): StoredOpPreferences {
  if (!hasLocalStorage()) {
    return createEmptyStore()
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return createEmptyStore()
    }

    const parsed = JSON.parse(raw)
    if (!isRecord(parsed) || parsed.version !== STORAGE_VERSION || !isRecord(parsed.ops)) {
      return createEmptyStore()
    }

    const ops: Record<string, Record<string, unknown>> = {}
    Object.entries(parsed.ops).forEach(([op, value]) => {
      const decodedValue = decodeFromJson(value)
      if (isRecord(decodedValue)) {
        ops[op] = decodedValue
      }
    })

    return {
      version: STORAGE_VERSION,
      ops,
    }
  } catch {
    return createEmptyStore()
  }
}

function writeLocalStore(store: StoredOpPreferences) {
  if (!hasLocalStorage()) {
    return
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(encodeForJson(store)))
  } catch {
    // Ignore storage quota and private-mode write failures.
  }
}

function readStore(): StoredOpPreferences {
  if (!shouldPersistToLocalStorage) {
    return memoryStore
  }

  return readLocalStore()
}

function writeStore(store: StoredOpPreferences) {
  if (!shouldPersistToLocalStorage) {
    memoryStore = store
    return
  }

  writeLocalStore(store)
}

function toPersistableValue(value: unknown): unknown {
  if (value === undefined) {
    return undefined
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => toPersistableValue(item))
      .filter((item) => item !== OMIT_PREFERENCE_VALUE)
  }

  if (!isRecord(value)) {
    return OMIT_PREFERENCE_VALUE
  }

  const result: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, nestedValue]) => {
    const persistable = toPersistableValue(nestedValue)
    if (persistable !== OMIT_PREFERENCE_VALUE) {
      result[key] = persistable
    }
  })

  return result
}

function createAllowedKeySet(keys: Iterable<string> | null | undefined) {
  const keySet = new Set<string>()
  for (const key of keys ?? []) {
    if (key) {
      keySet.add(key)
    }
  }
  return keySet
}

function sanitizePreference(
  value: Record<string, unknown>,
  keys: Iterable<string> | null | undefined,
) {
  const allowedKeys = createAllowedKeySet(keys)
  const preference: Record<string, unknown> = {}
  Object.entries(value).forEach(([key, rawValue]) => {
    if (!allowedKeys.has(key)) {
      return
    }

    const persistable = toPersistableValue(rawValue)
    if (persistable !== OMIT_PREFERENCE_VALUE) {
      preference[key] = persistable
    }
  })

  return preference
}

function hasKeys(value: Record<string, unknown>) {
  return Object.keys(value).length > 0
}

export function readOpPreference(
  op: string,
  keys: Iterable<string> | null | undefined,
): Record<string, unknown> | undefined {
  const stored = readStore().ops[op]
  if (!isRecord(stored)) {
    return undefined
  }

  const preference = sanitizePreference(stored, keys)
  return hasKeys(preference) ? preference : undefined
}

export function replaceOpPreference(
  op: string,
  value: Record<string, unknown>,
  keys: Iterable<string> | null | undefined,
) {
  if (!op) {
    return
  }

  const store = readStore()
  const preference = sanitizePreference(value, keys)
  if (hasKeys(preference)) {
    store.ops[op] = preference
  } else {
    delete store.ops[op]
  }
  writeStore(store)
}

export function patchOpPreference(
  op: string,
  patch: Record<string, unknown>,
  keys: Iterable<string> | null | undefined,
) {
  if (!op) {
    return
  }

  const store = readStore()
  const current = isRecord(store.ops[op])
    ? sanitizePreference(store.ops[op], keys)
    : {}
  const next = { ...current }
  const allowedKeys = createAllowedKeySet(keys)

  Object.entries(patch).forEach(([key, rawValue]) => {
    if (!allowedKeys.has(key)) {
      return
    }

    const persistable = toPersistableValue(rawValue)
    if (persistable !== OMIT_PREFERENCE_VALUE) {
      next[key] = persistable
    }
  })

  if (hasKeys(next)) {
    store.ops[op] = next
  } else {
    delete store.ops[op]
  }
  writeStore(store)
}

export function clearOpPreference(op: string) {
  if (!op) {
    return
  }

  const store = readStore()
  delete store.ops[op]
  writeStore(store)
}

export function clearOpPreferenceFields(op: string, fields: Iterable<string>) {
  if (!op) {
    return
  }

  const store = readStore()
  const current = store.ops[op]
  if (!isRecord(current)) {
    return
  }

  const next = { ...current }
  for (const field of fields) {
    delete next[field]
  }

  if (hasKeys(next)) {
    store.ops[op] = next
  } else {
    delete store.ops[op]
  }
  writeStore(store)
}

export function setOpPreferenceLocalStorageEnabled(enabled: boolean) {
  if (shouldPersistToLocalStorage === enabled) {
    return
  }

  if (!enabled) {
    memoryStore = readLocalStore()
    shouldPersistToLocalStorage = false
    return
  }

  shouldPersistToLocalStorage = true
  writeLocalStore(memoryStore)
}

export function isOpPreferenceLocalStorageEnabled() {
  return shouldPersistToLocalStorage
}
